import { useQuery } from '@tanstack/react-query';
import { apiFetch, type ApiError } from './client';
import { useAuth } from '../auth/useAuth';

/**
 * 配置引导(OFB)状态。对应后端 `GET /admin/onboarding/status`
 * (docs/specs/2026-06-02-out-of-box-onboarding.md §6.3;挂在 /admin/ 下走
 * scope gate,仅 admin:read 可见)。skip / complete 写接口已实现
 * (`PATCH /admin/onboarding/items/:key`、`POST /admin/onboarding/complete`)。
 *
 * useOnboardingStatus 在 404/401 时返回 null —— 旧部署或未登录时页面仍能渲染。
 */
export type ItemStatus = 'active' | 'partial' | 'missing' | 'skipped' | 'error';

export interface OnboardingStatus {
  license: { status: ItemStatus; licenseTier: string; expiresAt: string | null };
  authentication: {
    status: ItemStatus;
    methods: string[];
    oidcEnabled: boolean;
    imOauthEnabled: boolean;
  };
  autoApiKey: { status: ItemStatus; defaultScopes: string[] };
  tokenSources: { status: ItemStatus; activeCount: number };
  wallets: { status: ItemStatus; activeCount: number; unlimitedSelected: boolean };
  members: { status: ItemStatus; activeUsers: number; usersWithKeys: number };
  minimumCallable: { status: ItemStatus; missing: string[] };
  providerConfigured: boolean;
  modelsPublished: boolean;
  currentUserApiKeyCreated: boolean;
  firstCallReady: boolean;
  firstCallSucceeded: boolean;
  /** 配置完成时间(ISO),未完成为 null。 */
  completedAt: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isItemStatus(value: unknown): value is ItemStatus {
  return (
    value === 'active' ||
    value === 'partial' ||
    value === 'missing' ||
    value === 'skipped' ||
    value === 'error'
  );
}

function isOnboardingStatus(value: unknown): value is OnboardingStatus {
  if (!isRecord(value)) return false;
  const minimumCallable = value['minimumCallable'];
  const tokenSources = value['tokenSources'];
  const license = value['license'];
  return (
    isRecord(minimumCallable) &&
    isItemStatus(minimumCallable['status']) &&
    Array.isArray(minimumCallable['missing']) &&
    isRecord(tokenSources) &&
    isItemStatus(tokenSources['status']) &&
    typeof tokenSources['activeCount'] === 'number' &&
    isRecord(license) &&
    isItemStatus(license['status']) &&
    typeof value['providerConfigured'] === 'boolean' &&
    typeof value['modelsPublished'] === 'boolean' &&
    typeof value['currentUserApiKeyCreated'] === 'boolean' &&
    typeof value['firstCallReady'] === 'boolean' &&
    typeof value['firstCallSucceeded'] === 'boolean'
  );
}

/**
 * 拉取配置引导状态。后端缺失(404)或任何错误时返回 `data: undefined`,
 * 由页面退回本地交互模型,不抛错、不阻塞渲染。
 */
export function useOnboardingStatus() {
  const { tenant, user } = useAuth();
  return useQuery<OnboardingStatus | null>({
    queryKey: ['onboarding-status', tenant?.id ?? null, user?.id ?? null],
    queryFn: async () => {
      try {
        const data = await apiFetch<unknown>('/admin/onboarding/status');
        return isOnboardingStatus(data) ? data : null;
      } catch (e) {
        // 接口未实现 / 未登录 — 退回本地模型,不让页面崩。
        if ((e as ApiError).status === 404 || (e as ApiError).status === 401) return null;
        return null;
      }
    },
    retry: false,
    staleTime: 30_000,
  });
}

/** 跳过 / 恢复某配置项(authentication / tokenSources / wallets / members)。失败时抛错。 */
export async function patchOnboardingItem(
  itemKey: string,
  status: 'skipped' | 'active',
): Promise<void> {
  await apiFetch(`/admin/onboarding/items/${encodeURIComponent(itemKey)}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

/**
 * 标记配置完成。仅当后端判定最小可调用满足时返回 200;否则抛 ApiError
 * (status 409,body.missing 列出缺口),由 UI 提示。
 */
export async function completeOnboarding(): Promise<{ completedAt: string }> {
  return apiFetch<{ completedAt: string }>('/admin/onboarding/complete', { method: 'POST' });
}
